import Button from '@components/Button';
import Icon from '@material-ui/core/Icon';
import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import BreadCrumbs from '@components/Breadcrumbs';
import CircularProgress from '@material-ui/core/CircularProgress';

const permissions = ['administrador'];

const empty = { account: 2, start_date: '', end_date: '', note: '' };

const AsaasAccounts = (props) => {
  document.title = `Contas Asaas - ${process.env.REACT_APP_NAME}`;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ accounts: [], periods: [], active_account: null });
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { props.setShowMenu(false); }, []);
  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    apiClient.httpInstance.get('/asaas-accounts')
      .then(r => { if (r.status === 200) setData(r.data); })
      .catch(() => window.alert('Erro ao carregar as contas Asaas.'))
      .finally(() => setLoading(false));
  };

  const accountName = (id) => data.accounts.find(a => Number(a.id) === Number(id))?.name || `Conta ${id}`;

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      account: p.account,
      start_date: (p.start_date || '').slice(0, 10),
      end_date: (p.end_date || '').slice(0, 10),
      note: p.note || '',
    });
  };

  const cancelEdit = () => { setEditId(null); setForm(empty); };

  const save = () => {
    if (!form.start_date) { window.alert('Informe a data de início.'); return; }
    setSaving(true);
    const payload = {
      account: Number(form.account),
      start_date: form.start_date,
      end_date: form.end_date || null,
      note: form.note || null,
    };
    const req = editId
      ? apiClient.httpInstance.put(`/asaas-account-periods/${editId}`, payload)
      : apiClient.httpInstance.post('/asaas-account-periods', payload);
    req
      .then(() => { cancelEdit(); load(); })
      .catch((e) => window.alert(e?.response?.data?.message || 'Não foi possível salvar o período.'))
      .finally(() => setSaving(false));
  };

  const remove = (id) => {
    if (!window.confirm('Remover este período?')) return;
    apiClient.httpInstance.delete(`/asaas-account-periods/${id}`)
      .then(() => load())
      .catch(() => window.alert('Não foi possível remover.'));
  };

  const fmt = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—';

  if (!permissions.includes(props?.theme?.permission)) {
    return <div className="px-7 pt-6 text-blue">Acesso restrito ao administrador.</div>;
  }

  return <>
    <BreadCrumbs items={[{ title: 'Contas Asaas' }]} />
    <div className="px-7 pt-4">
      <div className="flex items-center mb-4 text-2xl text-blue">
        <Icon className="mr-2">account_balance</Icon>
        <span>Contas Asaas — período por empresa</span>
      </div>

      {loading ? (
        <div className="grid w-full justify-items-center py-10"><CircularProgress size={40} /></div>
      ) : (
        <>
          <div className="p-4 mb-5 bg-white" style={{ borderRadius: 10 }}>
            <span className="text-gray">Conta ativa hoje (recebe novas cobranças):</span>
            <div className="mt-1 text-xl text-blue font-poppins">
              <Icon className="mr-2" style={{ verticalAlign: 'middle', color: '#1d9e75' }}>check_circle</Icon>
              {accountName(data.active_account)} <span className="text-gray">(conta {data.active_account})</span>
            </div>
            <small className="text-gray">Fora dos períodos cadastrados, usa a conta padrão {data.default_account}.</small>
          </div>

          <div className="p-4 mb-5 bg-white" style={{ borderRadius: 10 }}>
            <div className="mb-3 text-lg text-blue">{editId ? 'Editar período' : 'Novo período'}</div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col text-sm text-gray">Empresa
                <select className="p-2 mt-1 border rounded border-lightgray" value={form.account}
                  onChange={e => setForm({ ...form, account: e.target.value })}>
                  {data.accounts.map(a => <option key={a.id} value={a.id}>{a.name} (conta {a.id})</option>)}
                </select>
              </label>
              <label className="flex flex-col text-sm text-gray">Início
                <input type="date" className="p-2 mt-1 border rounded border-lightgray"
                  value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </label>
              <label className="flex flex-col text-sm text-gray">Fim (opcional)
                <input type="date" className="p-2 mt-1 border rounded border-lightgray"
                  value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </label>
              <label className="flex flex-col flex-1 text-sm text-gray">Observação
                <input type="text" className="p-2 mt-1 border rounded border-lightgray" placeholder="ex: virada de mês"
                  value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
              </label>
              <Button textcolor="#fff" padding="8px 18px" margintop="0px" onClick={save}
                customcolor={props.theme.color} disabled={saving}>
                {editId ? 'Salvar' : 'Adicionar'}
              </Button>
              {editId && <Button textcolor="#e8f0fb" variant="outlined" padding="8px 18px" margintop="0px"
                bgColor="transparent" onClick={cancelEdit}>Cancelar</Button>}
            </div>
            <small className="text-gray">Deixe o Fim em branco para um período aberto (vale daqui pra frente).</small>
          </div>

          <div className="bg-white" style={{ borderRadius: 10 }}>
            <table className="w-full text-sm text-blue">
              <thead>
                <tr className="text-left text-gray border-b border-lightgray">
                  <th className="p-3">Empresa</th><th className="p-3">Início</th><th className="p-3">Fim</th>
                  <th className="p-3">Observação</th><th className="p-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.periods.length === 0 && (
                  <tr><td colSpan="5" className="p-4 text-gray">Nenhum período cadastrado — usando a conta padrão {data.default_account}.</td></tr>
                )}
                {data.periods.map(p => (
                  <tr key={p.id} className="border-b border-lightgray">
                    <td className="p-3">{accountName(p.account)} <span className="text-gray">({p.account})</span></td>
                    <td className="p-3">{fmt((p.start_date || '').slice(0, 10))}</td>
                    <td className="p-3">{p.end_date ? fmt((p.end_date || '').slice(0, 10)) : <span className="text-gray">aberto</span>}</td>
                    <td className="p-3">{p.note || '—'}</td>
                    <td className="p-3 text-right">
                      <Icon className="mr-3 cursor-pointer" onClick={() => startEdit(p)}>edit</Icon>
                      <Icon className="cursor-pointer" style={{ color: '#F44336' }} onClick={() => remove(p.id)}>delete</Icon>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  </>;
};

export default AsaasAccounts;
